import { fetchOpenAIText } from "../utils/openaiClient";

// Re-randomize option order on each item so the correct answer isn't
// always at the position the LLM happened to put it. We saw the model
// anchor to index 0 (variant A) after we put `correct: 0` in the JSON
// example, but really we shouldn't trust ANY model placement — shuffle
// and re-derive the index ourselves.
const shuffleMCOptions = (item) => {
  const opts = Array.isArray(item?.options) ? item.options : null;
  if (!opts || opts.length < 2) return item;
  const correctIdx = typeof item.correct === 'number' ? item.correct : 0;
  if (correctIdx < 0 || correctIdx >= opts.length) return item;
  const correctValue = opts[correctIdx];
  const shuffled = opts.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  // Find where the correct value landed. If duplicates exist, prefer the
  // first match — but generators shouldn't produce duplicate options.
  const newCorrect = shuffled.indexOf(correctValue);
  return { ...item, options: shuffled, correct: newCorrect >= 0 ? newCorrect : correctIdx };
};
const shuffleMCResult = (parsed) => {
  if (!parsed) return parsed;
  if (Array.isArray(parsed.sentences)) {
    return { ...parsed, sentences: parsed.sentences.map(shuffleMCOptions) };
  }
  if (Array.isArray(parsed.gaps)) {
    return { ...parsed, gaps: parsed.gaps.map(shuffleMCOptions) };
  }
  return parsed;
};

// Word-count / grammar-scope guidance per CEFR level for fill-in-the-blank
// sentences — mirrors the calibration table used for ABCD questions, but
// tuned for single-sentence gap-fill items rather than reading-comprehension
// questions about a passage.
const MC_CEFR_CALIBRATION = {
  A1: '4–7 word sentences; only present simple / "to be" / basic present continuous; concrete everyday nouns and verbs. AVOID: past tense, modals beyond "can", phrasal verbs.',
  A2: '6–10 word sentences; past simple, "going to" future, basic modals (can, must, should, have to); everyday topics. AVOID: perfect tenses, conditionals, passive voice.',
  B1: '8–14 word sentences; past continuous, present perfect, first conditional, common phrasal verbs. AVOID: subjunctive, inversion, advanced academic vocabulary.',
  B2: '10–18 word sentences; all perfect tenses, second/third conditional, passive voice, reported speech, common idioms. AVOID: rare literary structures.',
  C1: '12–22 word sentences; complex subordinate clauses, inversion for emphasis, hedging language, nuanced collocations, academic/journalistic register.',
  C2: '14–26 word sentences; near-native register; distractors that fail only on subtle connotation, idiomaticity, or discourse-marker choice.',
};

export function useMultipleChoiceGeneratorLogic({
  multipleChoiceInput, setMultipleChoiceInput, multipleChoiceMode, setMultipleChoiceMode,
  multipleChoiceLevel, multipleChoiceOptionCount, multipleChoiceCount,
  setMultipleChoiceLoading, generatedMultipleChoice, setGeneratedMultipleChoice,
  currentTab, onInsertToBoard, activeEditor,
  setShowMultipleChoiceGenerator, setSelectedTemplate,
}) {
  const handleMultipleChoiceGenerate = async () => {
    if (!multipleChoiceInput.trim()) return;
    setMultipleChoiceLoading(true);
    try {
      const isText = multipleChoiceMode === 'text';
      const looksLikeContent = multipleChoiceInput.trim().split(/\n+/).length > 2
        || multipleChoiceInput.trim().split(/[.!?]/).filter(Boolean).length > 3;

      // Build a labels string for the prompt: 2 → "A/B", 3 → "A/B/C", 4 → "A/B/C/D"
      const optLabels = ['A','B','C','D'].slice(0, multipleChoiceOptionCount).join('/');
      const sampleOptions = Array.from({ length: multipleChoiceOptionCount },
        (_, i) => `"word${i + 1}"`).join(',');
      const cefrLine = `CEFR level: ${multipleChoiceLevel} — ${MC_CEFR_CALIBRATION[multipleChoiceLevel] || 'match the target level strictly'}`;

      if (isText) {
        const prompt = `${looksLikeContent
          ? `Use the following text as the passage. Create ${multipleChoiceCount} multiple choice gap-fill items from it.`
          : `Generate a short reading passage of 80-120 words about: ${multipleChoiceInput}. Then create ${multipleChoiceCount} multiple choice gap-fill items from it.`}

${cefrLine}
${looksLikeContent ? `Text:\n${multipleChoiceInput}` : ''}

For each gap, remove a target chunk from the passage and mark its position with [N] (e.g. [1], [2]...). Provide EXACTLY ${multipleChoiceOptionCount} options (${optLabels}), one correct. Every gap MUST have ${multipleChoiceOptionCount} options — no more, no fewer.

CRITICAL RULES (do not violate):
- Each option is a COMPLETE grammatical answer — it must include EVERY word that belongs in the gap. If the target tense or structure uses an auxiliary or modal verb, include it in every option.
  Correct: "is reading", "has been working", "was eaten", "should have known", "didn't go", "going to leave"
  Wrong:   "reading" (missing "is"), "been working" (missing "has"), "eaten" (missing "was")
- Strict separation between passage and options. The passage contains ONLY the parts that stay constant. Any word that appears in an option must NOT appear in the passage next to the [N] marker; any word that appears in the passage must NOT appear in any option.
  Wrong:   "...They [1] watching a movie...",      options include "are watching" → "are watching watching"
  Right:   "...They [1] a movie at the moment...", options = ["are watching","is watching","were watch","watching are"]

  Wrong:   "...I [1] not studying this week...",   options = ["isn't","wasn't","am not","do not"]  → "am not not studying"
  Right:   "...I [1] not studying this week...",   options = ["am","is","was","do"]  (only the auxiliary is missing; "not" stays in the passage)
  Right:   "...I [1] studying this week...",       options = ["am not","is not","was not","do not"]  (full negative is missing; "not" must not stay in the passage)
  Decide UP FRONT which words live in the passage vs the options for each gap, and keep that line consistent across all distractors of the same gap.
- Self-check before returning: mentally insert the correct option in place of [N] and read that sentence aloud. If you see any word repeated ("not not", "watching watching", "is is"), if a word is missing, or if the sentence is ungrammatical, FIX it — adjust either the passage or the options so the substitution is clean.
- When you insert the correct option in place of [N], the passage must form a grammatically perfect sentence — no double auxiliaries, no missing words.
- Distractors should be plausible (wrong tense / wrong agreement / wrong word form) — not obviously off-topic.
- Distractors must be MEANINGFULLY different — never list the same answer twice in different surface forms. "is not" and "isn't" are the same answer; pick one. Same for "do not" / "don't", "have not" / "haven't", "I am" / "I'm", etc. Use the same convention (contracted OR full) across all options of a single gap.
- VARY the sentence forms in the passage — do not write a passage that is entirely affirmative declarative sentences. Include negative and interrogative sentences too, so the student practices the structure in all three forms.

Return ONLY valid JSON:
{
  "mode": "text",
  "title": "Multiple Choice",
  "level": "${multipleChoiceLevel}",
  "passage": "The passage text with [1] gap markers [2] inserted...",
  "gaps": [
    { "id": 1, "options": [${sampleOptions}], "correct": 0, "explanation": "Brief reason." }
  ]
}`;
        const content = await fetchOpenAIText({ prompt, temperature: 0.7, maxTokens: 1200 });
        if (content) {
          let parsed = JSON.parse(content.trim());
          if (Array.isArray(parsed.gaps) && parsed.gaps.length < multipleChoiceCount) {
            // One targeted retry: ask for the missing gaps only, appended
            // to the same passage, instead of silently under-delivering.
            const missing = multipleChoiceCount - parsed.gaps.length;
            const nextNum = parsed.gaps.length + 1;
            const fixPrompt = `You returned ${parsed.gaps.length} of ${multipleChoiceCount} requested gaps for this passage:\n\n${parsed.passage}\n\nExtend the SAME passage with ${missing} MORE gap(s), numbered starting at [${nextNum}], following the exact same rules as before (complete grammatical options, strict passage/option separation, EXACTLY ${multipleChoiceOptionCount} options each). Return ONLY valid JSON with the FULL updated passage (original + extension) and the FULL gaps array (${multipleChoiceCount} items total):\n{\n  "mode": "text",\n  "title": "${parsed.title || 'Multiple Choice'}",\n  "level": "${multipleChoiceLevel}",\n  "passage": "...",\n  "gaps": [ { "id": 1, "options": [${sampleOptions}], "correct": 0, "explanation": "..." } ]\n}`;
            const fixContent = await fetchOpenAIText({ prompt: fixPrompt, temperature: 0.7, maxTokens: 1200 });
            if (fixContent) {
              try {
                const fixed = JSON.parse(fixContent.trim());
                if (Array.isArray(fixed.gaps) && fixed.gaps.length >= parsed.gaps.length) parsed = fixed;
              } catch (_) { /* keep the original partial result */ }
            }
          }
          setGeneratedMultipleChoice(shuffleMCResult(parsed));
        }
      } else {
        const exampleOptions = multipleChoiceOptionCount === 2
          ? '"goes","went"'
          : multipleChoiceOptionCount === 3
            ? '"goes","went","going"'
            : '"go","goes","went","going"';
        const exampleCorrect = multipleChoiceOptionCount === 2 ? 1 : multipleChoiceOptionCount === 3 ? 1 : 2;
        const prompt = `${looksLikeContent
          ? `Create multiple choice fill-in-the-blank sentences from the following content. Extract or adapt ${multipleChoiceCount} sentences with one blank each.`
          : `Generate ${multipleChoiceCount} multiple choice fill-in-the-blank sentences about: ${multipleChoiceInput}.`}

${cefrLine}
${looksLikeContent ? `Content:\n${multipleChoiceInput}` : ''}

Each sentence has one blank marked as ___ and EXACTLY ${multipleChoiceOptionCount} options (${optLabels}), one correct. Every sentence MUST have ${multipleChoiceOptionCount} options — no more, no fewer.

CRITICAL RULES (do not violate):
- Each option is a COMPLETE grammatical answer — it must include EVERY word that belongs in the blank. If the target tense or structure uses an auxiliary or modal verb (continuous, perfect, perfect continuous, passive, modal + verb, "going to", negative auxiliaries, etc.), include it in every option.
  Correct: "is reading", "has been working", "was eaten", "should have known", "didn't go", "is going to leave"
  Wrong:   "reading" (missing "is"), "been working" (missing "has"), "eaten" (missing "was"), "have known" (missing "should")
- Strict separation between template and options. The sentence template contains ONLY the parts that stay constant. Any word that appears in an option must NOT appear in the template; any word that appears in the template must NOT appear in any option.
  Wrong:   text = "They ___ watching a movie",       options include "are watching"  → "are watching watching"
  Right:   text = "They ___ a movie at the moment",  options = ["are watching","is watching","were watch","watching are"]

  Wrong:   text = "I ___ not studying this week",    options = ["isn't","wasn't","am not","do not"]  → "am not not studying" or distractors that don't fit
  Right:   text = "I ___ not studying this week",    options = ["am","is","was","do"]  (only the auxiliary is missing; "not" stays in the template)
  Right:   text = "I ___ studying this week",        options = ["am not","is not","was not","do not"]  (full negative is missing; "not" must not stay in the template)
  Decide UP FRONT which words live in the template vs the options, and keep that line consistent across all distractors of the same question.
- Self-check before returning: mentally insert the correct option in place of ___ and read the sentence aloud. If you see any word repeated ("not not", "watching watching", "is is"), if a word is missing, or if the sentence is ungrammatical, FIX it — adjust either the template or the options so the substitution is clean.
- Build distractors as plausible mistakes (wrong tense, wrong agreement, wrong word form, missing auxiliary), not random words. They should fail the same grammar test the correct option passes.
- Distractors must be MEANINGFULLY different — never list the same answer twice in different surface forms. "is not" and "isn't" are the same answer; pick one. Same for "do not" / "don't", "have not" / "haven't", "I am" / "I'm", etc. Use the same convention (contracted OR full) across all options of a single question.
- VARY the sentence form across the set. Do not generate only affirmative declarative sentences. Roughly: about 1/3 affirmative, 1/3 negative, 1/3 interrogative (questions starting with auxiliary / wh-word), so the student practices the structure in all three forms. Adjust ratio to fit the grammar being taught, but never produce a set that is all-affirmative.

Return ONLY valid JSON:
{
  "mode": "sentences",
  "title": "Multiple Choice",
  "level": "${multipleChoiceLevel}",
  "sentences": [
    { "id": 1, "text": "She ___ to the store yesterday.", "options": [${exampleOptions}], "correct": ${exampleCorrect}, "explanation": "Past simple for a completed past action." }
  ]
}`;
        const content = await fetchOpenAIText({ prompt, temperature: 0.7, maxTokens: 1200 });
        if (content) {
          let parsed = JSON.parse(content.trim());
          if (Array.isArray(parsed.sentences) && parsed.sentences.length < multipleChoiceCount) {
            // One targeted retry: ask for the missing sentences only, as
            // NEW distinct items, instead of silently under-delivering.
            const missing = multipleChoiceCount - parsed.sentences.length;
            const existing = parsed.sentences.map((s, i) => `${i + 1}. ${s.text || ''}`).join('\n');
            const fixPrompt = `You returned ${parsed.sentences.length} of ${multipleChoiceCount} requested multiple-choice fill-in-the-blank sentences.\n\nAdd ${missing} MORE sentences to reach ${multipleChoiceCount} total. They must be DIFFERENT from the ones already generated (different content/vocabulary), following the exact same rules as before (complete grammatical options, strict template/option separation, EXACTLY ${multipleChoiceOptionCount} options each).\n\n${cefrLine}\n\nExisting sentences (do not repeat these):\n${existing}\n\nReturn ONLY valid JSON with the FULL sentences array (existing + new, ${multipleChoiceCount} items total):\n{\n  "mode": "sentences",\n  "title": "${parsed.title || 'Multiple Choice'}",\n  "level": "${multipleChoiceLevel}",\n  "sentences": [ { "id": 1, "text": "...", "options": [${exampleOptions}], "correct": ${exampleCorrect}, "explanation": "..." } ]\n}`;
            const fixContent = await fetchOpenAIText({ prompt: fixPrompt, temperature: 0.7, maxTokens: 1200 });
            if (fixContent) {
              try {
                const fixed = JSON.parse(fixContent.trim());
                if (Array.isArray(fixed.sentences) && fixed.sentences.length >= parsed.sentences.length) parsed = fixed;
              } catch (_) { /* keep the original partial result */ }
            }
          }
          setGeneratedMultipleChoice(shuffleMCResult(parsed));
        }
      }
    } catch (e) {
      // Error generating multiple choice
    } finally {
      setMultipleChoiceLoading(false);
    }
  };

  const handleMultipleChoiceInsert = () => {
    if (!generatedMultipleChoice) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedMultipleChoice, 'multiplechoice');
      setShowMultipleChoiceGenerator(false);
      setMultipleChoiceInput('');
      setGeneratedMultipleChoice(null);
      setSelectedTemplate(null);
      return;
    }
    if (activeEditor) {
      const { mode, sentences, passage, gaps, title, level } = generatedMultipleChoice;
      activeEditor.chain().focus().insertMultipleChoiceExercise({ mode, sentences, passage, gaps, title, level }).run();
    }
    setShowMultipleChoiceGenerator(false);
    setMultipleChoiceInput('');
    setGeneratedMultipleChoice(null);
    setSelectedTemplate(null);
  };

  const handleMultipleChoiceCancel = () => {
    setShowMultipleChoiceGenerator(false);
    setMultipleChoiceInput('');
    setGeneratedMultipleChoice(null);
    setMultipleChoiceMode('sentences');
    setSelectedTemplate(null);
  };

  return { handleMultipleChoiceGenerate, handleMultipleChoiceInsert, handleMultipleChoiceCancel };
}
